import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import FAQ from '../components/FAQ';
import ProductCard from '../components/ProductCard';
import DownloadAppButton from '../components/DownloadAppButton';
import { fetchProductFeed } from '../services/productService';
import FeesIcon from '../icons/FeesIcon';
import CheckIcon from '../icons/CheckIcon';
import DeliveryIcon from '../icons/DeliveryIcon';
import CardIcon from '../icons/CardIcon';

function Home() {
  const [unsoldProducts, setUnsoldProducts] = useState([]);
  const [soldProducts, setSoldProducts] = useState([]);
  const [showSold, setShowSold] = useState(false);

  useEffect(() => {
    // Skip during react-snap's prerender crawl so the static snapshot only
    // captures the page shell; products always load dynamically in the browser.
    if (navigator.userAgent === 'ReactSnap') return;

    fetchProductFeed({ unsold: true }).then(setUnsoldProducts).catch(console.error);
    fetchProductFeed({ sold: true }).then(setSoldProducts).catch(console.error);
  }, []);

  const filteredProducts = (showSold ? soldProducts : unsoldProducts).filter((p) => p.price > 50);

  return (
    <main className="w-full">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col relative bg-no-repeat bg-cover bg-center hero-bg z-1 overflow-hidden">
        {/* Nav */}
        <div className="fixed top-0 z-50 px-4 md:px-[72px] py-4 md:py-[16px] w-full" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
          <div className="bg-white border border-[#e6e8e3] rounded-[24px] flex items-center justify-between p-4 md:p-[32px]">
            <img
              src="/images/logo_full_green.png"
              alt="WearTwice Logo"
              width="120"
              height="48"
            />
            <DownloadAppButton label="Download app" />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center px-4 md:px-[72px] py-8 md:py-[64px]">
          <div className="flex flex-col md:flex-col items-start md:items-start justify-between w-full gap-16">
            {/* Left: text + CTAs */}
            <div className="flex flex-col gap-8">
              <h1 className="text-4xl text-left md:text-[64px] font-extrabold leading-tight md:leading-[64px] text-white">
                Sell easily,<br/>Buy sustainably
              </h1>
              <p className="hidden md:block text-base text-left md:text-[18px] md:leading-[28px] text-white md:max-w-[520px]">
                Buy, sell and swap quality fashion in the UAE. A smarter, more affordable and more sustainable way to shop.
              </p>
              <DownloadAppButton additionalClasses='self-start'/>
            </div>

            <div className="flex space-x-4">
              <a target="_blank" rel="noreferrer" className="w-36" href="https://apps.apple.com/us/app/weartwice/id6733228175">
                <img src="/images/Type=Default.svg" alt="Download on the App Store" style={{width: '100%', height: 'auto'}} />
              </a>
              <a target="_blank" rel="noreferrer" className="w-36" href="https://play.google.com/store/apps/details?id=com.weartwice.weartwice">
                <img src="/images/Type=Default (1).svg" alt="Get it on Google Play" style={{width: '100%', height: 'auto'}} />
              </a>
            </div>

            {/* Right: phone mockup (desktop only) */}
            <img
              src="/images/mobile-hero.png"
              alt="WearTwice App"
              className="hidden md:block h-[815px] w-auto object-contain absolute right-[72px] bottom-[-195px]"
            />
            
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="w-full bg-[#FFF] flex flex-col items-center gap-[64px] px-6 md:px-[72px] py-[64px] z-2">
        {/* Title */}
        <div className="flex flex-col gap-4 items-center text-center max-w-[880px] w-full">
          <h2 className="text-4xl md:text-[64px] font-bold md:leading-[64px] text-[#137c63]">
            The best of pre-loved fashion across the UAE
          </h2>
          <p className="text-base leading-[28px] text-[#252d2a]">
            Discover sought after luxury labels and everyday favourites like Louis Vuitton, Chanel, Gucci, Zara, Nike, Adidas and many more, available across the UAE with secure in app payments and 24 hour delivery.
          </p>
        </div>

        {/* Shop by category */}
        <div className="flex flex-col gap-4 max-w-[1296px] w-full">
          <p className="font-semibold text-[21px] text-left text-[#062319]">Shop by category</p>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 h-[100px] rounded-lg overflow-hidden bg-[#062319] flex items-center gap-2 px-8">
              <span className="flex-1 font-semibold text-[18px] text-[#DDFDB3] text-left">Women</span>
              <img src="/images/women-icon.png" alt="" className="shrink-0 size-[100px] object-cover" />
            </div>
            <div className="flex-1 h-[100px] rounded-lg overflow-hidden bg-[#062319] flex items-center gap-2 px-8">
              <span className="flex-1 font-semibold text-[18px] text-[#DDFDB3] text-left">Men</span>
              <img src="/images/men-icon.png" alt="" className="shrink-0 size-[100px] object-cover" />
            </div>
            <div className="flex-1 h-[100px] rounded-lg overflow-hidden bg-[#062319] flex items-center gap-2 px-8">
              <span className="flex-1 font-semibold text-[18px] text-[#DDFDB3] text-left">Kids</span>
              <img src="/images/kids-icon.png" alt="" className="shrink-0 size-[100px] object-cover" />
            </div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex justify-center gap-2 w-full max-w-[1296px]">
          <button onClick={() => setShowSold(false)} className={`flex items-center gap-1 text-sm px-2 py-1 rounded-lg ${!showSold ? 'bg-[#062319] text-[#DDFDB3]' : 'border border-[#062319] text-[#062319]'}`}>
            Available
            {!showSold && <CheckIcon />}
          </button>
          <button onClick={() => setShowSold(true)} className={`flex items-center gap-1 text-sm px-2 py-1 rounded-lg ${showSold ? 'bg-[#062319] text-[#DDFDB3]' : 'border border-[#062319] text-[#062319]'}`}>
            Just sold
            {showSold && <CheckIcon />}
          </button>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2 gap-y-6 max-w-[1296px] w-full">
          {filteredProducts.slice(0, 8).map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* App download banner */}
        <div className="relative flex flex-col md:flex-row items-center justify-between max-w-[1296px] w-full md:h-[300px] rounded-[24px] overflow-hidden bg-[#eeeeee] px-[48px] pt-[32px] md:pt-0">
          <div className="flex flex-col gap-4 max-w-[480px] z-10">
            <p className="font-bold text-left text-2xl text-[#062319]">Want to see more? Download the WearTwice app!</p>
            <p className="text-base text-left leading-[28px] text-[#252d2a]">Browse thousands of pre loved items, make offers and enjoy 24 hour delivery across the UAE.</p>
            <DownloadAppButton additionalClasses='self-start'/>
          </div>
          <img
              src="/images/mobiles.png"
              alt="WearTwice App"
              className="relative md:right-8 md:top-16 h-[240px] md:h-[340px] w-auto object-contain"
          />
        </div>
      </section>

      {/* Key Features Section */}
      <section className="w-full bg-[#0a3928] py-[64px] px-6 flex flex-col items-center gap-[64px]">
        {/* Title block */}
        <div className="flex flex-col gap-2 items-center text-center text-white max-w-[880px] w-full">
          <h2 className="text-4xl md:text-[64px] font-bold md:leading-[64px]">
            Clear out your wardrobe and earn money
          </h2>
          <p className="text-base leading-[28px]">
            Discover a simple and fast way to buy and sell pre loved fashion across the UAE. Shop second hand clothes, accessories and premium brands at better prices, with secure in app payments and 24 hour delivery.
          </p>
        </div>

        {/* Feature cards */}
        <div className="flex flex-col md:flex-row gap-4 items-stretch justify-center w-full">
          {/* Zero selling fees */}
          <div className="bg-white/10 rounded-lg flex flex-col gap-5 items-center pt-8 px-8 pb-4 md:w-[252px]">
            <div className="flex flex-col gap-1 items-center w-full">
              <div className="bg-[#0a3928] p-2 rounded-lg">
                <FeesIcon />
              </div>
              <p className="font-bold text-base text-center text-white">Zero selling fees</p>
              <p className="font-bold text-[10px] text-center text-white">Sellers keep 100% of the sale price. No hidden fees.</p>
            </div>
          </div>

          {/* 1 business day delivery */}
          <div className="bg-white/10 rounded-lg flex flex-col gap-5 items-center justify-between pt-8 px-8 pb-4 md:w-[252px]">
            <div className="flex flex-col gap-1 items-center w-full">
              <div className="bg-[#0a3928] p-2 rounded-lg">
                <DeliveryIcon />
              </div>
              <p className="font-bold text-base text-center text-white">1 business day delivery</p>
              <p className="font-bold text-[10px] text-center text-white">Fast delivery across the UAE</p>
            </div>
            <div className="bg-white px-2 py-2 rounded-lg flex items-center justify-center h-10 w-16">
              <img src="/images/ajex.png" alt='AJEX'/>
            </div>
          </div>

          {/* Secure payments */}
          <div className="bg-white/10 rounded-lg flex flex-col gap-5 items-center justify-between pt-8 px-8 pb-4 md:w-[252px]">
            <div className="flex flex-col gap-1 items-center w-full">
              <div className="bg-[#0a3928] p-2 rounded-lg">
                <CardIcon />
              </div>
              <p className="font-bold text-base text-center text-white">Secure payments</p>
              <p className="font-bold text-[10px] text-center text-white">Protected payments through our trusted partner Mamo.</p>
            </div>
            <div className="bg-white px-2 py-2 rounded-lg flex items-center justify-center h-10 w-16">
              <img src="/images/mamo.svg" alt='Mamo Pay'/>
            </div>
          </div>
        </div>

        {/* CTA */}
        <DownloadAppButton />
      </section>

      {/* CTA 2 Section */}
      <section className="w-full bg-[#f3f4f0] py-[64px] px-6 md:px-[72px] flex flex-col items-center gap-[64px]">
        {/* Headline */}
        <div className="flex flex-col items-center max-w-[880px] w-full text-center">
          <h2 className="text-4xl md:text-[64px] font-bold md:leading-[64px] text-[#137c63]">
            A smarter and more sustainable way to shop fashion
          </h2>
        </div>

        {/* CTA */}
        <DownloadAppButton />

        {/* Cards */}
        <div className="flex flex-col lg:flex-row gap-4 items-stretch w-full">
          {/* Sellers Card */}
          <div
            className={`relative flex flex-col overflow-hidden rounded-[24px] h-[540px] md:h-[670px] min-h-[540px] md:min-h-[670px] flex-1`}
          >
            <div className="absolute inset-0 bg-[#062319]" />
            <img src="/images/ImageA-03.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="relative z-10 p-6 md:p-12 flex flex-col h-full">
              <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col gap-1">
                <h3 className="text-[32px] text-left font-bold text-[#137c63]">Sellers</h3>
                <p className="text-xs text-left text-[#252d2a]">
                  Turn your wardrobe into <strong>cash</strong>. List your pre loved clothes and accessories <strong>for free</strong> with WearTwice.
                </p>
              </div>
            </div>
          </div>

          {/* Buyers Card */}
          <div
            className={`relative flex flex-col overflow-hidden rounded-[24px] h-[540px] md:h-[670px] min-h-[540px] md:min-h-[670px] flex-1`}
          >
            <div className="absolute inset-0 bg-[#062319]" />
            <img src="/images/ImageB-03.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="relative z-10 p-6 md:p-12 flex flex-col h-full">
              <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col gap-1">
                <h3 className="text-[32px] text-left font-bold text-[#137c63]">Buyers</h3>
                <p className="text-xs text-left text-[#252d2a]">
                  Shop pre-loved fashion at <strong>better prices</strong>. Enjoy <strong>secure payments</strong> with Mamo and <strong>1 business day delivery</strong> across the UAE.
                </p>
              </div>
            </div>
          </div>

          {/* Make an offer Card */}
          <div
            className={`relative flex flex-col overflow-hidden rounded-[24px] h-[540px] md:h-[670px] min-h-[540px] md:min-h-[670px] flex-1`}
          >
            <div className="absolute inset-0 bg-[#062319]" />
            <img src="/images/ImageC-03.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="relative z-10 p-6 md:p-12 flex flex-col h-full">
              <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col gap-1">
                <h3 className="text-[32px] text-left font-bold text-[#137c63]">Make an offer</h3>
                <p className="text-xs text-left text-[#252d2a]">
                  <strong>Everything is negotiable.</strong> Make offers, receive counter offers and save on pre loved fashion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Short version */}
      <FAQ limit={5} showTitle={true} />

      {/* Security Section */}
      <section className="w-full flex flex-col items-center px-6 md:px-[72px] py-[64px] bg-[#FFF]">
        <div className="relative flex flex-col gap-[64px] h-[400px] items-center justify-center max-w-[1296px] w-full px-6 md:px-[72px] rounded-[24px] overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[#062319]" />
            <img alt="" className="absolute inset-0 w-full h-full object-cover" src="/images/section_ reassurance.jpg" />
          </div>
          <div className="relative flex flex-col gap-4 items-center max-w-[880px] w-full text-center">
            <h2 className="text-4xl md:text-[64px] font-bold text-[#DDFDB3]">
              Security and trust
            </h2>
            <p className="text-base leading-[28px] text-white">
              Buy and sell pre loved fashion with secure payments, protected purchases and peace of mind.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

export default Home;
